import { Injectable, signal, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  createDragImageForLargeElements,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import { SetDemarcatorRange } from '@wilson/shift-timeline/state';
import { Store } from '@ngxs/store';
import { LARGE_ELEMENT_PIXEL_SIZE } from '@wilson/interfaces';

@Injectable()
export class TimelineShiftDragAndDropDirectiveHelperService {
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly store = inject(Store);

  private isShiftVisibleSubject = new BehaviorSubject(true);
  private tooltipVisiblitySubject = new BehaviorSubject(true);
  private readonly shiftLeftPixelSignal = signal<number | null>(null);
  private readonly shiftRightPixelSignal = signal<number | null>(null);
  private readonly shiftPreviewSignal = signal<Element | null>(null);
  private readonly shiftDurationSignal = signal<number>(0);

  public isShiftVisible$ = this.isShiftVisibleSubject.asObservable();
  public isTooltipVisible$ = this.tooltipVisiblitySubject.asObservable();

  initiateCellViewDragAndDrop(isAssignmentMode: boolean): void {
    setTimeout(() => {
      this.isShiftVisibleSubject.next(isAssignmentMode);
      if (isAssignmentMode) {
        this.dragAndDropModeService.setShiftDropZoneVisible(true);
      } else {
        this.dragAndDropModeService.setShiftDropZoneVisible(false);
      }
    });
  }

  initiateDragAndDrop(
    isAssignmentMode: boolean,
    event: DragEvent,
    elementWidth: number | null,
  ): void {
    if (elementWidth && elementWidth > LARGE_ELEMENT_PIXEL_SIZE) {
      createDragImageForLargeElements(event);
    }
    const previewElement = this.shiftPreviewSignal();
    if (previewElement && !isAssignmentMode) {
      event.dataTransfer?.setDragImage(previewElement, 0, 0);
    }
    setTimeout(() => {
      this.isShiftVisibleSubject.next(isAssignmentMode);
      this.store.dispatch(
        new SetDemarcatorRange([
          {
            left: this.shiftLeftPixelSignal(),
            right: this.shiftRightPixelSignal(),
          },
        ]),
      );
      if (isAssignmentMode) {
        this.dragAndDropModeService.setTimeDifferenceVisible(true);
        this.dragAndDropModeService.setIsMovingItemLeftRight(false);
        this.dragAndDropModeService.setShiftDropZoneVisible(true);
      } else {
        this.dragAndDropModeService.setTimeDifferenceVisible(false);
        this.dragAndDropModeService.setShiftDropZoneVisible(false);
        this.dragAndDropModeService.setIsMovingItemLeftRight(true);
      }
    });
  }

  trackHorizontalDrag(event: DragEvent & { layerX: number }): void {
    const left = this.shiftLeftPixelSignal();
    const right = this.shiftRightPixelSignal();
    if (left && right) {
      const trackedLeftPosition = event.layerX;
      const offsetInPixels = left - trackedLeftPosition;
      const trackedRightPosition = right + offsetInPixels;
      const range = {
        left:
          trackedLeftPosition -
          DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL,
        right:
          trackedRightPosition +
          DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL,
        shiftDuration: this.shiftDurationSignal(),
        adjustmentNeeded: true,
      };
      this.dragAndDropModeService.setHorizontalDemarcatorRange(range);
    }
  }

  handleCellViewDragEnd(): void {
    this.tooltipVisiblitySubject.next(true);
    this.dragAndDropModeService.setShiftDropZoneVisible(false);
    this.isShiftVisibleSubject.next(true);
    this.dragAndDropModeService.setDraggingShiftV2(null);
  }

  handleDragEnd(isMoveMode: boolean): void {
    if (isMoveMode) {
      this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
    }
    this.tooltipVisiblitySubject.next(true);
    this.dragAndDropModeService.setTimeDifferenceVisible(true);
    this.dragAndDropModeService.setShiftDropZoneVisible(true);
    this.dragAndDropModeService.setIsMovingItemLeftRight(false);
    this.dragAndDropModeService.setDisplayTimeVisible(false);
    this.dragAndDropModeService.setHorizontalDemarcatorRange({
      left: null,
      right: null,
      shiftDuration: null,
      adjustmentNeeded: true,
    });
    this.isShiftVisibleSubject.next(true);
  }

  setShiftVisibility(isShiftVisible: boolean): void {
    this.isShiftVisibleSubject.next(isShiftVisible);
  }

  setIsTooltipVisible(isTooltipVisible: boolean): void {
    this.tooltipVisiblitySubject.next(isTooltipVisible);
  }

  setShiftPreviewCard(shiftPreviewCard: Element): void {
    this.shiftPreviewSignal.set(shiftPreviewCard);
  }

  setShiftLeftPixelPosition(leftPixelPosition: number): void {
    this.shiftLeftPixelSignal.set(leftPixelPosition);
  }

  setShiftRightPixelPosition(rightPixelPosition: number): void {
    this.shiftRightPixelSignal.set(rightPixelPosition);
  }

  setShiftDuration(shiftDuration: number): void {
    this.shiftDurationSignal.set(shiftDuration);
  }
}
